import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
import composeRefs from '@seznam/compose-react-refs';
import { useFeatureFlag } from '@moda/portal-stanchions';
import { LazyLoad, LazyLoadProps, LazyLoadContext } from '../LazyLoad';
import { newRelicClient } from '../../lib/newrelicClient';
import { useImageSources, ImageSourceOptions } from './useImageSources';

import './Image.scss';

export type ImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> &
  ImageSourceOptions &
  LazyLoadProps & {
    onError?(): void;
    onLoad?(): void;
  };

enum Mode {
  Resting,
  Error
}

export const Image = React.forwardRef(
  (
    {
      src: rawSrc,
      alt,
      onError,
      onLoad,
      className,
      lazyload,
      width,
      height,
      srcMode = 'dynamic',
      nativeImageSize,
      quality,
      ...rest
    }: ImageProps,
    forwardedRef: React.Ref<HTMLImageElement>
  ) => {
    const useImageErrorReporting = useFeatureFlag('send-image-errors-to-nr', [
      { default: false },
      { if: true, then: true }
    ]);

    const { enable: enableLazyLoad } = useContext(LazyLoadContext);
    const [mode, setMode] = useState(Mode.Resting);

    const ref = React.useRef<HTMLImageElement>(null);

    const { src, srcSet } = useImageSources({
      src: rawSrc,
      quality,
      nativeImageSize,
      srcMode
    });

    const isLazyLoaded = enableLazyLoad && lazyload !== false;

    const handleError = useCallback(() => {
      setMode(Mode.Error);
      onError && onError();
      if (useImageErrorReporting)
        newRelicClient.noticeError({ name: 'Image Loading Error', message: src });
    }, [onError, src, useImageErrorReporting]);

    useEffect(() => {
      if (ref.current?.complete) {
        onLoad?.();
      }
    }, [onLoad, ref]);

    useEffect(() => {
      setMode(Mode.Resting);
    }, [rawSrc]);

    return (
      <LazyLoad lazyload={lazyload}>
        {!isLazyLoaded && (
          <Helmet>
            <link
              rel="preload"
              as="image"
              href={src}
              {...(srcSet ? { imagesrcset: srcSet } : {})}
            />
          </Helmet>
        )}
        <img
          alt={alt}
          className={classNames(`Image Image--${Mode[mode].toLowerCase()}`, className)}
          height={height}
          onError={handleError}
          onLoad={onLoad}
          src={src}
          srcSet={srcSet}
          ref={composeRefs(ref, forwardedRef)}
          width={width}
          {...rest}
        />
      </LazyLoad>
    );
  }
);

Image.displayName = 'Image';
